/**
 * Stable anonymous user identity – persisted in localStorage.
 * Used for analytics enrichment and push preference keying.
 */

const STORAGE_KEY = 'anonUserId';
const ANON_ID_RE = /^[0-9a-f]{16}$/;

let cached: string | null = null;

export function isValidAnonUserId(value: unknown): value is string {
  return typeof value === 'string' && ANON_ID_RE.test(value);
}

/** Test helper — drop in-memory cache between suites. */
export function clearAnonIdentityCache(): void {
  cached = null;
}

function generate16Hex(): string {
  try {
    const buf = new Uint8Array(8);
    crypto.getRandomValues(buf);
    return Array.from(buf, b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    // Fallback for environments without crypto
    let hex = '';
    for (let i = 0; i < 16; i++) {
      hex += Math.floor(Math.random() * 16).toString(16);
    }
    return hex;
  }
}

/**
 * Returns a stable 16-hex-char anonymous user ID.
 * Created once, persisted in localStorage, cached in memory.
 */
export function getAnonUserId(): string {
  if (cached) return cached;

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isValidAnonUserId(stored)) {
      cached = stored;
      return cached;
    }
  } catch { /* localStorage unavailable */ }

  const id = generate16Hex();
  cached = id;

  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch { /* ignore */ }

  return id;
}
